import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';

import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ItemViewContainerComponent } from '@shared/components/item-view-container.component';
import { CountrySectorData, FlattenedCountrySector } from '@features/watchlists/models/watchlist-country-sector.model';
import { transformDateString } from '@shared/utils/date-string-transform.utils';

type SectorDataToDisplay = {
  sectorName: string;
  validToDate: string;
  lastUpdated: string;
};

@Component({
  selector: 'app-country-sector-detail-dialog',
  imports: [MatDialogModule, MatButtonModule, MatIconModule, ItemViewContainerComponent],
  template: `
    <h2 mat-dialog-title>Country Sectors details ({{ countryName }})</h2>

    <mat-dialog-content>
      @for (sector of sectors; track sector) {
        <div class="detail-row">
          <app-item-view-container label="Sector Name" class="detail-3-item">{{ sector.sectorName }}</app-item-view-container>

          <app-item-view-container label="Last Update" class="detail-item"> {{ sector.lastUpdated }}</app-item-view-container>

          <app-item-view-container label="Valid To Date" class="detail-item">{{ sector.validToDate }}</app-item-view-container>
        </div>
      }
    </mat-dialog-content>

    <mat-dialog-actions>
      <button mat-button mat-dialog-close cdkFocusInitial>Close</button>
    </mat-dialog-actions>
  `,
  styles: `
    .detail-row {
      display: flex;
      gap: 16px;
    }

    .detail-3-item {
      flex: 3;
      min-width: 0;
    }

    .detail-item {
      flex: 1;
      min-width: 0; /* Allow flex items to shrink below their content width */
    }

    .detail-chip {
      display: inline-block;
      padding: 0.25rem 0.75rem;
      margin: 0.25rem;
      border: 1px solid var(--text-80-color);
      border-radius: 8px;
    }

    mat-dialog-content {
      min-width: 500px;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CountrySectorDetailDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<CountrySectorDetailDialogComponent>);
  readonly data = inject<FlattenedCountrySector>(MAT_DIALOG_DATA);

  countryName!: string;
  sectors!: SectorDataToDisplay[];

  ngOnInit(): void {
    this.countryName = this.data.countryName;
    this.sectors = Object.entries(this.data)
      .filter(([key, value]) => key !== 'countryName' && value && typeof value === 'object')
      .map(([sectorName, value]) => ({
        sectorName: sectorName,
        validToDate: transformDateString((value as CountrySectorData).validTo),
        lastUpdated: (value as CountrySectorData).lastReviewed ? transformDateString((value as CountrySectorData).lastReviewed) : 'N/A',
      }));
  }
}
